import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, TextField, IconButton, Box } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { Getsettings, UpdateBaseSettings } from "../redux/slices/getsettings";
import { Updatesettings } from "../redux/slices/updateSettings";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import { keyframes } from "@emotion/react";

// Define the rotation animation
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const schema = yup.object().shape({
  games: yup.array().of(
    yup.object().shape({
      gameId: yup.string().required(),
      cookie: yup.string().required("Cookie is required!"),
    })
  ),
});

const SettingsForm = () => {
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dispatch = useDispatch();
  const settingsData = useSelector((state) => state.gsr.data);
  const isLoading = useSelector((state) => state.gsr.loading);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      games:
        (settingsData?.length > 0 &&
          settingsData?.map((item) => ({
            gameId: item._id || "",
            cookie: item.cookie || "",
          }))) ||
        [],
    },
  });

  useEffect(() => {
    dispatch(Getsettings());
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(Getsettings());
      reset({
        games:
          settingsData?.map((item) => ({
            gameId: item._id || "",
            cookie: item.cookie || "",
          })) || [],
      });
    } catch (error) {
      toast.error("Failed to refresh settings");
    } finally {
      setIsRefreshing(false);
    }
  };

  const onSubmit = async (index, data) => {
    setLoadingIndex(index);
    try {
      const updatedData = {
        _id: settingsData?.[index]?._id,
        host: settingsData?.[index]?.host,
        gameId: data.games[index].gameId,
        cookie: data.games[index].cookie,
      };
      await dispatch(UpdateBaseSettings(updatedData));
      toast.success("Settings updated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to update settings");
    } finally {
      setLoadingIndex(null);
    }
  };

  return (
    <div className="w-full mx-auto px-4 py-6">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <h3 className="text-3xl font-semibold text-gray-900">
          Games Base Settings
          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{
              ml: 2,
              backgroundColor: "#3f51b5",
              color: "white",
              "&:hover": {
                backgroundColor: "#303f9f",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
              },
              animation: isLoading ? `${rotate} 1s linear infinite` : "none",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </h3>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        {settingsData?.length > 0 &&
          settingsData?.map((game, index) => (
            <div
              key={game._id}
              className="flex items-center gap-4 w-full bg-white shadow-lg rounded-xl p-6 mb-4 transition-transform transform hover:scale-105"
            >
              <span className="w-1/4 text-lg font-medium text-gray-800">
                {game.host}
              </span>
              <div className="w-full">
                <Controller
                  name={`games.${index}.gameId`}
                  control={control}
                  rules={{ required: "Game ID is required" }}
                  defaultValue={game._id}
                  render={({ field }) => <input type="hidden" {...field} />}
                />

                <Controller
                  name={`games.${index}.cookie`}
                  control={control}
                  defaultValue={game.cookie}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cookie"
                      variant="standard"
                      fullWidth
                      error={!!errors?.games?.[index]?.cookie}
                      helperText={errors?.games?.[index]?.cookie?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "#3f51b5",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3f51b5",
                          },
                        },
                      }}
                    />
                  )}
                />
              </div>
              <button
                type="button"
                onClick={handleSubmit((data) => onSubmit(index, data))}
                disabled={loadingIndex === index || isRefreshing}
                className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-base font-medium transition-transform transform hover:scale-105 disabled:opacity-70 disabled:transform-none"
              >
                {loadingIndex === index ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Update"
                )}
              </button>
            </div>
          ))}
      </form>
    </div>
  );
};

export default SettingsForm;
